if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.hoverMql = window.matchMedia('(hover: hover) and (pointer: fine)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnailSwitchers();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        // Vertical thumbnail chrome is PDP-only; skip inside quick-add modal
        if (!this.closest('quick-add-modal')) {
          this.initThumbnailScrollbar();
          this.initVerticalThumbnailNav();
        }
      }

      getThumbnailList() {
        return (
          this.elements.thumbnails?.slider ||
          this.elements.thumbnails?.querySelector('[id^="Slider-Thumbnails"]')
        );
      }

      isVerticalThumbnails() {
        return Boolean(this.elements.thumbnails?.classList.contains('thumbnail-slider--vertical'));
      }

      bindThumbnailSwitchers(scope = this.elements.thumbnails) {
        if (!scope) return;
        scope.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button || button.dataset.mediaSwitchBound === 'true') return;
          button.dataset.mediaSwitchBound = 'true';

          const activate = () => this.setActiveMedia(mediaToSwitch.dataset.target, false);
          button.addEventListener('click', activate);
          // Desktop: switch main image on hover
          button.addEventListener('mouseenter', () => {
            if (!this.hoverMql.matches) return;
            activate();
          });
        });
      }

      initVerticalThumbnailNav() {
        const thumbs = this.elements.thumbnails;
        if (!thumbs || !this.isVerticalThumbnails()) return;

        const list = this.getThumbnailList();
        const prev = thumbs.querySelector('.slider-button--prev');
        const next = thumbs.querySelector('.slider-button--next');
        if (!list || !prev || !next) return;

        const desktopMql = window.matchMedia('(min-width: 990px)');
        const VISIBLE = 9;

        const getItemMetrics = () => {
          const item = list.querySelector('.thumbnail-list__item');
          if (!item) return { itemHeight: 64, gap: 8, step: 72 };
          const gap = parseFloat(getComputedStyle(list).rowGap || getComputedStyle(list).gap) || 8;
          const itemHeight = item.getBoundingClientRect().height || item.offsetHeight || 64;
          return { itemHeight, gap, step: itemHeight + gap };
        };

        const getItemCount = () => list.querySelectorAll('.thumbnail-list__item').length;

        const applyScrollport = () => {
          if (!desktopMql.matches) {
            list.style.height = '';
            list.style.maxHeight = '';
            list.style.overflowY = '';
            return getItemCount() > VISIBLE;
          }

          const count = getItemCount();
          const hasOverflow = count > VISIBLE;
          thumbs.classList.toggle('thumbnail-slider--scrollable', hasOverflow);

          if (hasOverflow) {
            const { itemHeight, gap } = getItemMetrics();
            const maxH = itemHeight * VISIBLE + gap * (VISIBLE - 1);
            list.style.height = `${maxH}px`;
            list.style.maxHeight = `${maxH}px`;
            list.style.overflowY = 'auto';
            list.style.overscrollBehavior = 'contain';
          } else {
            list.style.height = '';
            list.style.maxHeight = '';
            list.style.overflowY = '';
          }

          return hasOverflow;
        };

        const setEdgeState = (button, atEdge) => {
          // Never use native [disabled] — SliderComponent + disabled blocks clicks
          button.removeAttribute('disabled');
          button.classList.toggle('is-disabled', atEdge);
          button.setAttribute('aria-disabled', atEdge ? 'true' : 'false');
        };

        const updateButtons = () => {
          if (!desktopMql.matches) return;

          const hasOverflow = applyScrollport();
          if (!hasOverflow) {
            setEdgeState(prev, true);
            setEdgeState(next, true);
            return;
          }

          const { scrollTop, scrollHeight, clientHeight } = list;
          setEdgeState(prev, scrollTop <= 1);
          setEdgeState(next, scrollTop + clientHeight >= scrollHeight - 1);
        };

        const scrollByDir = (dir) => {
          if (!desktopMql.matches) return;
          applyScrollport();
          const { step } = getItemMetrics();
          list.scrollBy({ top: dir * step, behavior: 'smooth' });
          // sync after smooth scroll settles
          window.setTimeout(updateButtons, 320);
        };

        // Stop Dawn SliderComponent from disabling both arrows (horizontal visibility logic)
        if (typeof thumbs.update === 'function' && !thumbs._verticalUpdatePatched) {
          const originalUpdate = thumbs.update.bind(thumbs);
          thumbs.update = () => {
            if (desktopMql.matches) {
              updateButtons();
              return;
            }
            originalUpdate();
          };
          thumbs._verticalUpdatePatched = true;
        }

        // Clear any disabled attrs SliderComponent already applied
        prev.removeAttribute('disabled');
        next.removeAttribute('disabled');

        prev.addEventListener(
          'click',
          (event) => {
            if (!desktopMql.matches) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            if (prev.classList.contains('is-disabled')) return;
            scrollByDir(-1);
          },
          true
        );
        next.addEventListener(
          'click',
          (event) => {
            if (!desktopMql.matches) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            if (next.classList.contains('is-disabled')) return;
            scrollByDir(1);
          },
          true
        );

        // Capture wheel on the whole thumbs column so page/section doesn't steal it
        const onWheel = (event) => {
          if (!desktopMql.matches) return;
          if (getItemCount() <= VISIBLE) return;

          applyScrollport();
          event.preventDefault();
          event.stopPropagation();
          list.scrollTop += event.deltaY;
          updateButtons();
        };
        thumbs.addEventListener('wheel', onWheel, { passive: false, capture: true });
        list.addEventListener('wheel', onWheel, { passive: false, capture: true });

        this.verticalThumbUpdate = updateButtons;
        list.addEventListener('scroll', updateButtons, { passive: true });
        new ResizeObserver(updateButtons).observe(list);
        window.addEventListener('resize', updateButtons, { passive: true });
        requestAnimationFrame(() => {
          requestAnimationFrame(updateButtons);
        });
      }

      initThumbnailScrollbar() {
        const slider = this.getThumbnailList();
        const thumb = this.elements.thumbnails?.querySelector('.thumbnail-scrollbar__thumb');
        const track = this.elements.thumbnails?.querySelector('.thumbnail-scrollbar__track');
        const scrollbar = this.elements.thumbnails?.querySelector('.thumbnail-scrollbar');
        if (!slider || !thumb || !track || !scrollbar) return;

        const updateScrollbar = () => {
          const { scrollLeft, scrollWidth, clientWidth } = slider;
          const maxScroll = scrollWidth - clientWidth;

          if (maxScroll <= 0) {
            scrollbar.hidden = true;
            return;
          }

          scrollbar.hidden = false;
          const trackWidth = track.clientWidth;
          const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 24);
          const maxThumbLeft = trackWidth - thumbWidth;
          const thumbLeft = (scrollLeft / maxScroll) * maxThumbLeft;

          thumb.style.width = `${thumbWidth}px`;
          thumb.style.transform = `translateX(${thumbLeft}px)`;
        };

        this.thumbnailScrollbarUpdate = updateScrollbar;
        slider.addEventListener('scroll', updateScrollbar, { passive: true });
        new ResizeObserver(updateScrollbar).observe(slider);
        updateScrollbar();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        this.thumbnailScrollbarUpdate?.();
        this.verticalThumbUpdate?.();
      }

      isThumbnailInView(thumbnail, list, offset = 4) {
        if (!thumbnail || !list) return true;
        if (this.isVerticalThumbnails() && window.matchMedia('(min-width: 990px)').matches) {
          const top = thumbnail.offsetTop;
          const bottom = top + thumbnail.offsetHeight;
          return top >= list.scrollTop - offset && bottom <= list.scrollTop + list.clientHeight + offset;
        }
        if (typeof this.elements.thumbnails.isSlideVisible === 'function') {
          return this.elements.thumbnails.isSlideVisible(thumbnail, 10);
        }
        return true;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        const thumbnailSlider = this.getThumbnailList();
        if (!thumbnailSlider) return;
        if (this.isThumbnailInView(thumbnail, thumbnailSlider)) return;

        if (this.isVerticalThumbnails() && window.matchMedia('(min-width: 990px)').matches) {
          thumbnailSlider.scrollTo({ top: thumbnail.offsetTop, behavior: 'smooth' });
        } else {
          thumbnailSlider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      replaceWithVariantImages(images, sectionId) {
        if (!images?.length) return;

        const resolvedSectionId = sectionId || this.id.replace(/^MediaGallery-/, '');
        const viewerList = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        if (!viewerList) return;

        const mediaFit = this.dataset.mediaFit || 'contain';
        const constrain = this.dataset.constrain === 'true';
        const imageZoom = this.dataset.imageZoom || 'lightbox';
        const sizes =
          this.dataset.imageSizes ||
          '(min-width: 1200px) calc((1200px - 10rem) / 2), (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
        const thumbSizes =
          this.dataset.thumbSizes ||
          '(min-width: 990px) 64px, (min-width: 750px) calc((100vw - 11.5rem) / 8), calc((100vw - 8rem) / 3)';

        viewerList.innerHTML = images
          .map((image, index) => {
            const mediaId = `${resolvedSectionId}-variant-media-${index}`;
            const ratio = image.aspect_ratio || 1;
            const height = Math.round(1946 / ratio);
            const isActive = index === 0 ? ' is-active' : '';
            const loading = index === 0 ? 'eager' : 'lazy';

            return `
              <li
                id="Slide-${resolvedSectionId}-variant-media-${index}"
                class="product__media-item grid__item slider__slide${isActive}"
                data-media-id="${mediaId}"
              >
                <div
                  class="product-media-container media-type-image media-fit-${mediaFit} global-media-settings gradient${
                    constrain ? ' constrain-height' : ''
                  }"
                  style="--ratio: ${ratio}; --preview-ratio: ${ratio};"
                >
                  <modal-opener
                    class="product__modal-opener product__modal-opener--image"
                    data-modal="#ProductModal-${resolvedSectionId}"
                  >
                    <span
                      class="product__media-icon motion-reduce quick-add-hidden product__media-icon--${imageZoom}"
                      aria-hidden="true"
                    >
                      <span class="svg-wrapper">
                        <svg class="icon icon-zoom" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M8.791 8.791a.7.7 0 0 1 .99 0l3.214 3.215a.7.7 0 1 1-.99.99L8.792 9.78a.7.7 0 0 1 0-.99Z" fill="currentColor"/>
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M5.95 2.1a3.85 3.85 0 1 0 0 7.7 3.85 3.85 0 0 0 0-7.7ZM.7 5.95a5.25 5.25 0 1 1 10.5 0 5.25 5.25 0 0 1-10.5 0Z" fill="currentColor"/>
                        </svg>
                      </span>
                    </span>
                    <div class="product__media media media--transparent">
                      <img
                        class="image-magnify-${imageZoom}"
                        src="${image.src}"
                        srcset="${image.srcset || `${image.src} 1946w`}"
                        sizes="${sizes}"
                        alt="${this.escapeHtml(image.alt || '')}"
                        loading="${loading}"
                        width="1946"
                        height="${height}"
                      >
                    </div>
                    <button
                      class="product__media-toggle quick-add-hidden product__media-zoom-${imageZoom}"
                      type="button"
                      aria-haspopup="dialog"
                      data-media-id="${mediaId}"
                    >
                      <span class="visually-hidden">Open media ${index + 1}</span>
                    </button>
                  </modal-opener>
                </div>
              </li>
            `;
          })
          .join('');

        const thumbList = this.elements.thumbnails?.querySelector('[id^="Slider-Thumbnails"]');
        if (thumbList) {
          thumbList.innerHTML = images
            .map((image, index) => {
              const mediaId = `${resolvedSectionId}-variant-media-${index}`;
              const thumbId = `Thumbnail-${resolvedSectionId}-variant-media-${index}`;
              const ariaCurrent = index === 0 ? ' aria-current="true"' : '';

              return `
                <li
                  id="Slide-Thumbnails-${resolvedSectionId}-variant-media-${index}"
                  class="thumbnail-list__item slider__slide"
                  data-target="${mediaId}"
                  data-media-position="${index + 1}"
                >
                  <button
                    class="thumbnail global-media-settings global-media-settings--no-shadow"
                    aria-label="Load image ${index + 1}"
                    ${ariaCurrent}
                    aria-controls="GalleryViewer-${resolvedSectionId}"
                    aria-describedby="${thumbId}"
                  >
                    <img
                      id="${thumbId}"
                      src="${image.thumb || image.src}"
                      alt="${this.escapeHtml(image.alt || '')}"
                      loading="lazy"
                      width="64"
                      height="64"
                      sizes="${thumbSizes}"
                    >
                  </button>
                </li>
              `;
            })
            .join('');

          this.bindThumbnailSwitchers(thumbList);
        }

        const counterTotal = this.querySelector('.slider-counter--total');
        if (counterTotal) counterTotal.textContent = String(images.length);

        const counterCurrent = this.querySelector('.slider-counter--current');
        if (counterCurrent) counterCurrent.textContent = '1';

        const sliderButtons = this.querySelector('.slider-buttons');
        if (sliderButtons) {
          sliderButtons.classList.toggle('small-hide', images.length <= 1);
        }

        this.updateModalContent(images, resolvedSectionId);

        if (this.elements.viewer.slider) this.elements.viewer.resetPages?.();
        this.setActiveMedia(`${resolvedSectionId}-variant-media-0`, true);
        this.thumbnailScrollbarUpdate?.();
        this.verticalThumbUpdate?.();

        if (this.closest('[data-zoom-on-hover]') && typeof enableZoomOnHover === 'function') {
          enableZoomOnHover(2);
        }
      }

      updateModalContent(images, sectionId) {
        const modalContent = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
        if (!modalContent) return;

        modalContent.innerHTML = images
          .map((image, index) => {
            const mediaId = `${sectionId}-variant-media-${index}`;
            const ratio = image.aspect_ratio || 1;
            const height = Math.round(1100 / ratio);

            return `
              <img
                class="global-media-settings global-media-settings--no-shadow"
                srcset="${image.srcset || `${image.src} 1946w`}"
                sizes="(min-width: 750px) calc(100vw - 22rem), 1100px"
                src="${image.src}"
                alt="${this.escapeHtml(image.alt || '')}"
                loading="lazy"
                width="1100"
                height="${height}"
                data-media-id="${mediaId}"
              >
            `;
          })
          .join('');
      }

      escapeHtml(value) {
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }
    }
  );
}
